import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw, AlertCircle, ArrowUpDown, ArrowUp, ArrowDown, Sun, Moon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMaoDeObra, getTodosDados } from '../api';
import MultiSelect from '../componets/MultiSelect';
import {
  PROJETOS_TABELA_NORMAL,
  MAPA_MESES_2025,
} from '../constants/projetos';


const MESES_DRE = [
  { value: 'Todos', label: 'Todos' },
  { value: 1,  label: 'Janeiro'   },
  { value: 2,  label: 'Fevereiro' },
  { value: 3,  label: 'Março'     },
  { value: 4,  label: 'Abril'     },
  { value: 5,  label: 'Maio'      },
  { value: 6,  label: 'Junho'     },
  { value: 7,  label: 'Julho'     },
  { value: 8,  label: 'Agosto'    },
  { value: 9,  label: 'Setembro'  },
  { value: 10, label: 'Outubro'   },
  { value: 11, label: 'Novembro'  },
  { value: 12, label: 'Dezembro'  },
];

// ─── Componente Principal ─────────────────────────────────────────────────────
const Dashboard = () => {

  // ── parseBRL (mantido idêntico ao margemBruta.js) ────────────────────────────
  const parseBRL = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    const str = String(value).trim();
    if (!str) return 0;
    let limpo = str.replace(/[^\d.,-]/g, '');
    const ultimaVirgula = limpo.lastIndexOf(',');
    const ultimoPonto   = limpo.lastIndexOf('.');
    if (ultimaVirgula > ultimoPonto) {
      limpo = limpo.replace(/\./g, '').replace(',', '.');
    } else if (ultimoPonto > ultimaVirgula) {
      limpo = limpo.replace(/,/g, '');
    } else if (ultimaVirgula !== -1) {
      limpo = limpo.replace(',', '.');
    }
    const numero = Number(limpo);
    return isNaN(numero) ? 0 : numero;
  };

  // ── States ───────────────────────────────────────────────────────────────────
  const [isLight, setIsLight] = useState(true);

  const [filters, setFilters] = useState({
    projeto: [],
    prestador: [],
    gerente: [],
    dataDre: [1,2,3],
    anoDre: [],
    margemProjetada: [],
  });


  const [dadosOriginais,      setDadosOriginais]      = useState([]);
  const [dadosLancamentos,    setDadosLancamentos]    = useState([]);
  const [dadosLancamentos2025,setDadosLancamentos2025]= useState([]);
  const [dadosCompetencia,    setDadosCompetencia]    = useState([]);
  const [dadosMaoDeObra,      setDadosMaoDeObra]      = useState([]);
  const [linhasExpandidas,    setLinhasExpandidas]    = useState({});
  const [loading,             setLoading]             = useState(false);
  const [error,               setError]               = useState(null);
  const [ordenacao, setOrdenacao] = useState({ 
    campo: 'valorBruto',
    direcao: 'desc'
  });
  const navigate = useNavigate();

  // ── Carregamento de dados (idêntico ao margemBruta.js) ───────────────────────
  useEffect(() => {
    console.log('🔄 Componente montado, carregando dados...');
    carregarDadosGoogleSheets();
  }, []);

  const carregarDadosGoogleSheets = async () => {
    setLoading(true);
    setError(null);
    console.log('📡 Iniciando requisição para o backend...');

    try {
      const [result, resultMaoDeObra] = await Promise.all([
        getTodosDados(),
        getMaoDeObra(),
      ]);

      if (!result.success) {
        throw new Error(result.error || 'Erro desconhecido ao buscar dados');
      }

      // ── Lançamentos ─────────────────────────────────────────────────────────
      const dadosLanc = result.lancamentos.data.map(item => {
        const dataCompleta = item['Data (completa)'] || '';
        let mes = null, ano = null;
        if (dataCompleta) {
          const partes = dataCompleta.split('/');
          if (partes.length === 3) { mes = Number(partes[1]); ano = Number(partes[2]); }
        }
        return {
          empresa:      item['Minha Empresa (Razão Social)'] || 'N/A',
          projeto:      item['Projeto']      || 'N/A',
          valor:        parseBRL(item['Valor'] || 0),
          departamento: item['Departamento'],
          data:         dataCompleta,
          mes,
          ano,
        };
      });

      // ── NFe ─────────────────────────────────────────────────────────────────
      const dadosNFe = result.nfe.data.map(item => ({

        projeto: item['Nome do Projeto'] || 'N/A',
        prestador: item['Prestador de serviço'] || 'N/A',
        gerente: item['Gerente de Projeto'] || 'N/A',
        valorBruto: (item['Valor Bruto'] || 0),
        margemBruta: (item['Margem Bruta do Contrato']) || 0,
        faturamentoLiquido: (item['Faturamento Liquido']) || 0,
        dataDre: Number(item['Mês DRE']) || null,
        anoDre: Number(item['Ano DRE']) || null,
        margemProjetada: item['Margem Bruta do Contrato'] || ''
      }));

      // ── Lançamentos 2025 ────────────────────────────────────────────────────
      const dadosLancc2025 = result.lancamentos2025.data.map(item => {
        const mesTexto   = item['Mês'] || '';
        const mesNumerico = MAPA_MESES_2025[mesTexto] || null;
        return {
          mes:               mesNumerico,
          gerente:           item['Gerente de Projetos']                             || 'N/A',
          projeto:           item['Nomes Projetos Oficiais']                         || 'N/A',
          maoDeObra:         parseBRL(item['Mão de Obra']                            || 0),
          outrosCustos:      parseBRL(item['Outros Custos (Viagem, freelance etc)']  || 0),
          outrosCustosRateio:parseBRL(item['Outros Custos Rateio']                   || 0),
          valorBruto:        parseBRL(item['Faturamento Bruto']                      || 0),
          margemBrutaReal:   parseBRL(item['Margem Bruta']                           || 0),
          faturamentoLiquido:parseBRL(item['Receita Líquida']                        || 0),
          anoDre:            2025,
        };
      });

      // ── Competência ─────────────────────────────────────────────────────────
      const dadosComp = (result.competencia?.data || []).map(item => {
        const compStr = item['Competência'] || '';
        const partes  = compStr.split('/');
        const mesComp = partes.length === 2 ? Number(partes[0]) : null;
        const anoComp = partes.length === 2 ? Number(partes[1]) : null;
        return {
          projeto:       item['Projeto']      || 'N/A',
          saldoBruto:    parseBRL(item['Saldo Bruto']   || 0),
          saldoLiquido:  parseBRL(item['Saldo Liquido'] || 0),
          mes:           mesComp,
          ano:           anoComp,
        };
      });

      setDadosCompetencia(dadosComp);
      setDadosOriginais(dadosNFe);
      setDadosLancamentos(dadosLanc);
      setDadosLancamentos2025(dadosLancc2025);

      // ── Mão de Obra ─────────────────────────────────────────────────────────
      // .trim() é essencial: nomes na planilha chegam com espaços extras
      if (resultMaoDeObra?.success && Array.isArray(resultMaoDeObra.data)) {
        const maoDeObraProcessada = resultMaoDeObra.data.map(item => {
          const mesStr = (item['mês'] || '').trim();
          const partes = mesStr.split('/');
          const mes = partes.length === 2 ? Number(partes[0]) : null;
          const ano = partes.length === 2 ? Number(partes[1]) : null;
          return {
            projeto: (item['PROJETO'] || 'N/A').trim(),
            valor:   Number(item['Valor']) || 0,
            mes,
            ano,
          };
        });
        setDadosMaoDeObra(maoDeObraProcessada);
      }

      setLoading(false);
    } catch (err) {
      console.error('❌ Erro ao carregar dados:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // ── Lógica de filtros ────────────────────────────────────────────────────────
  const is2025 = filters.anoDre.length === 1 && filters.anoDre[0] === 2025;

  const prestadores = ['Todos', ...new Set(dadosOriginais.map(d => d.prestador))];
  const gerentes    = ['Todos', ...new Set(dadosOriginais.map(d => d.gerente))];
  const anos = [2025 ,2026];

  const projetosSet = is2025
    ? new Set(dadosLancamentos2025.map(d => d.projeto))
    : new Set(dadosOriginais.map(d => d.projeto));

  const projetosOptions   = [...projetosSet].sort().map(p => ({ value: p, label: p }));
  const prestadoresOptions= prestadores.filter(p => p !== 'Todos').map(p => ({ value: p, label: p }));
  const gerentesOptions   = gerentes.filter(g => g !== 'Todos').map(g => ({ value: g, label: g }));
  const anosOptions       = anos.filter(a => a !== 'Todos').map(a => ({ value: a, label: a }));
  const mesesOptions      = MESES_DRE.filter(m => m.value !== 'Todos').map(m => ({ value: m.value, label: m.label }));

  // ── dadosFiltrados ───────────────────────────────────────────────────────────
  let dadosFiltrados = [];

  if (is2025) {
    dadosFiltrados = dadosLancamentos2025.filter(item =>
      (filters.projeto.length === 0 || filters.projeto.includes(item.projeto)) &&
      (filters.gerente.length === 0 || filters.gerente.includes(item.gerente)) &&
      (filters.dataDre.length === 0 || filters.dataDre.includes(item.mes))
    );
  } else {
    dadosFiltrados = dadosOriginais.filter(item => {
      if (item.anoDre === null || item.anoDre === '' || item.anoDre === 2024) return false;
      return (
        (filters.projeto.length   === 0 || filters.projeto.includes(item.projeto))   &&
        (filters.prestador.length === 0 || filters.prestador.includes(item.prestador)) &&
        (filters.gerente.length   === 0 || filters.gerente.includes(item.gerente))   &&
        (filters.dataDre.length   === 0 || filters.dataDre.includes(item.dataDre))   &&
        (filters.anoDre.length    === 0 || filters.anoDre.includes(item.anoDre))
      );
    });
  }

  // ── projetosAtivosNoFiltro ───────────────────────────────────────────────────
  // Conjunto derivado do dadosFiltrados — que já respeitou TODOS os filtros
  // (projeto, gerente, prestador, mês, ano).
  // É a ÚNICA fonte de verdade sobre quais projetos estão no escopo atual.
  // Usada como âncora implícita em todos os pipelines de custo para garantir
  // que faturamento e custo sempre enxerguem o mesmo universo de projetos.
  const projetosAtivosNoFiltro = new Set(dadosFiltrados.map(d => d.projeto));

  // Indica se algum filtro de escopo (gerente, prestador, projeto) está ativo.
  // Quando verdadeiro, os pipelines de custo se restringem a projetosAtivosNoFiltro.
  const temFiltroEscopo =
    filters.projeto.length > 0 ||
    filters.gerente.length > 0 ||
    filters.prestador.length > 0;

  // ── dadosLancFiltrados (apenas OPERAÇÃO, sem 2024) ───────────────────────────
  // SEPARAÇÃO LANCC vs LANCC2025:
  // dadosLancamentos  → planilha genérica, contém 2025-2026+, campo 'Data (completa)'
  //                     Usado SOMENTE no modo normal (não-2025).
  // dadosLancamentos2025 → planilha exclusiva 2025 com estrutura diferente
  //                        Usada SOMENTE no modo 2025 (já em dadosFiltrados).
  // No modo 2025, os custos já vêm embutidos em dadosFiltrados (maoDeObra,
  // outrosCustos, outrosCustosRateio), portanto dadosLancamentos NÃO É USADO.
  const dadosLancFiltrados = is2025 ? [] : dadosLancamentos.filter(item => {
    if (item.ano === null || item.ano === '' || item.ano === 2024) return false;
    if (item.departamento !== 'OPERAÇÃO') return false;
    // Herda o escopo de projetos do filtro ativo (inclui filtro de gerente/prestador
    // indiretamente, pois projetosAtivosNoFiltro já veio de dadosFiltrados filtrado).
    const projetoNoEscopo = !temFiltroEscopo || projetosAtivosNoFiltro.has(item.projeto);
    return (
      projetoNoEscopo &&
      (filters.dataDre.length === 0 || filters.dataDre.includes(item.mes)) &&
      (filters.anoDre.length  === 0 || filters.anoDre.includes(item.ano))
    );
  });

  // ── custosOperacionaisPorProjeto ─────────────────────────────────────────────
  const custosOperacionaisPorProjeto = dadosLancFiltrados.reduce((acc, item) => {
    if (!acc[item.projeto]) acc[item.projeto] = 0;
    acc[item.projeto] += Number(item.valor) || 0;
    return acc;
  }, {});

  // ── custosMaoDeObraPorProjeto ────────────────────────────────────────────────
  // SEPARAÇÃO: dadosMaoDeObra é exclusivo do modo normal.
  // No modo 2025 os custos de mão de obra já chegam via dadosLancamentos2025.
  const custosMaoDeObraPorProjeto = is2025 ? {} : dadosMaoDeObra
    .filter(item => {
      const projetoNoEscopo = !temFiltroEscopo || projetosAtivosNoFiltro.has(item.projeto);
      return (
        projetoNoEscopo &&
        (filters.dataDre.length === 0 || filters.dataDre.includes(item.mes)) &&
        (filters.anoDre.length  === 0 || filters.anoDre.includes(item.ano))
      );
    })
    .reduce((acc, item) => {
      acc[item.projeto] = (acc[item.projeto] || 0) + item.valor;
      return acc;
    }, {});

  // ── dadosAgrupados ───────────────────────────────────────────────────────────
  let dadosAgrupados = [];

  if (is2025) {
    const agrupado = {};
    dadosFiltrados.forEach(item => {
      if (!agrupado[item.projeto]) {
        agrupado[item.projeto] = {
          projeto:            item.projeto,
          gerente:            item.gerente,
          anoDre:             2025,
          valorBruto:         0,
          faturamentoLiquido: 0,
          maoDeObra:          0,
          outrosCustos:       0,
          outrosCustosRateio: 0,
          margemBrutaPercentual: 0,
          margemProjetada:    item.margemProjetada,
          detalhes:           [],
        };
      }
      agrupado[item.projeto].valorBruto          += item.valorBruto;
      agrupado[item.projeto].faturamentoLiquido  += item.faturamentoLiquido;
      agrupado[item.projeto].maoDeObra           += item.maoDeObra;
      agrupado[item.projeto].outrosCustos        += item.outrosCustos;
      agrupado[item.projeto].outrosCustosRateio  += item.outrosCustosRateio;
      agrupado[item.projeto].margemBrutaPercentual += item.margemBrutaPercentual;
      agrupado[item.projeto].detalhes.push(item);
    });
    dadosAgrupados = Object.values(agrupado);
  } else {
    dadosAgrupados = dadosFiltrados.reduce((acc, item) => {
      const projetoExistente = acc.find(p => p.projeto === item.projeto);
      if (projetoExistente) {
        projetoExistente.margemBruta        += item.margemBruta;
        projetoExistente.faturamentoLiquido += item.faturamentoLiquido;
        projetoExistente.valorBruto         += item.valorBruto;
      } else {
        acc.push({
          projeto:            item.projeto,
          gerente:            item.gerente,
          prestador:          item.prestador,
          dataDre:            item.dataDre,
          anoDre:             item.anoDre,
          margemBruta:        item.margemBruta,
          faturamentoLiquido: item.faturamentoLiquido,
          valorBruto:         item.valorBruto,
          margemProjetada:    item.margemProjetada,
        });
      }
      return acc;
    }, []);
  }

  // ── dadosTabela2025 ──────────────────────────────────────────────────────────
  const dadosTabela2025 = dadosAgrupados.map(item => {
    const valorBruto         = Number(item.valorBruto)         || 0;
    const faturamentoLiquido = Number(item.faturamentoLiquido) || 0;
    const outrosCustos       = Number(item.outrosCustos)       || 0;
    const outrosCustosRateio = Number(item.outrosCustosRateio) || 0;
    const maoDeObra          = Number(item.maoDeObra)          || 0;
    const custoOperacional   = outrosCustos + outrosCustosRateio;
    const margemBrutaReal    = faturamentoLiquido - custoOperacional - maoDeObra;
    const calcPct = (valor, base) =>
      !base || base === 0 ? 0 : ((valor / base) * 100).toFixed(2);
    return {
      projeto:               item.projeto,
      gerente:               item.gerente,
      valorBruto,
      faturamentoLiquido,
      custoOperacional,
      percentualCustoOp:     calcPct(custoOperacional, faturamentoLiquido),
      custoMaoDeObra:        maoDeObra,
      percentualMaoDeObra:   calcPct(maoDeObra, faturamentoLiquido),
      margemBrutaReal,
      percentualMargemBruta: calcPct(margemBrutaReal, faturamentoLiquido),
      detalhes:              item.detalhes,
    };
  });

  // ── faturamentoCompetenciaPorProjeto (apenas 2026+) ──────────────────────────
  const faturamentoCompetenciaPorProjeto = dadosCompetencia
    .filter(item => {
      if (!item.ano || item.ano < 2026) return false;
      return (
        (filters.projeto.length === 0 || filters.projeto.includes(item.projeto)) &&
        (filters.dataDre.length === 0 || filters.dataDre.includes(item.mes))     &&
        (filters.anoDre.length  === 0 || filters.anoDre.includes(item.ano))
      );
    })
    .reduce((acc, item) => {
      if (!acc[item.projeto]) acc[item.projeto] = { valorBruto: 0, faturamentoLiquido: 0 };
      acc[item.projeto].valorBruto          += item.saldoBruto;
      acc[item.projeto].faturamentoLiquido  += item.saldoLiquido;
      return acc;
    }, {});

  // ── dadosTabelaNormal (usando PROJETOS_TABELA_NORMAL + FATURAMENTO_2026) ──────
  // Quando há filtro de escopo ativo, itera apenas sobre os projetos que estão
  // em projetosAtivosNoFiltro ∩ PROJETOS_TABELA_NORMAL.
  // Isso evita que projetos de outros gerentes/prestadores entrem na tabela com
  // faturamento = 0 e custos distorcidos, que é a causa raiz da inconsistência
  // de margem ao filtrar por gerente.
  const listaProjetosTabela = temFiltroEscopo
    ? PROJETOS_TABELA_NORMAL.filter(p => projetosAtivosNoFiltro.has(p))
    : PROJETOS_TABELA_NORMAL;

  const dadosTabelaNormal = listaProjetosTabela.map(projeto => {
    const item = dadosAgrupados.find(d => d.projeto === projeto);
    let faturamentoLiquido = Number(item?.faturamentoLiquido) || 0;
    let valorBruto         = Number(item?.valorBruto)         || 0;

    valorBruto         += faturamentoCompetenciaPorProjeto[projeto]?.valorBruto         || 0;
    faturamentoLiquido += faturamentoCompetenciaPorProjeto[projeto]?.faturamentoLiquido || 0;

    const custoOperacional = (custosOperacionaisPorProjeto[projeto] * -1) || 0;
    const custoMaoDeObra   = custosMaoDeObraPorProjeto[projeto] || 0;
    const margemBrutaReal  = faturamentoLiquido - custoMaoDeObra - custoOperacional;
    const calcPct = (valor, base) =>
      base > 0 ? ((valor / base) * 100).toFixed(2) : '0.00';

    return {
      projeto,
      gerente:               item?.gerente || '-',
      valorBruto,
      faturamentoLiquido,
      custoOperacional,
      percentualCustoOp:     calcPct(custoOperacional, faturamentoLiquido),
      custoMaoDeObra,
      percentualMaoDeObra:   calcPct(custoMaoDeObra, faturamentoLiquido),
      margemBrutaReal,
      percentualMargemBruta: calcPct(margemBrutaReal, faturamentoLiquido),
      margemProjetada:       item?.margemProjetada || '-',
    };
  });

  // ── RATEIO DE OPERAÇÕES ──────────────────────────────────────────────────────
  // Distribui proporcionalmente (por faturamentoLiquido) o custo do projeto
  // "RATEIO DE OPERAÇÕES" entre os demais e o remove da tabela.
  const NOME_RATEIO = 'RATEIO DE OPERAÇÕES';
  const dadosTabelaNormalComRateio = (() => {
    const projetoRateio = dadosTabelaNormal.find(p => p.projeto === NOME_RATEIO);

    if (!projetoRateio || projetoRateio.custoOperacional === 0) {
      return dadosTabelaNormal.filter(p => p.projeto !== NOME_RATEIO);
    }

    const custoParaRatear = projetoRateio.custoOperacional;
    const demais          = dadosTabelaNormal.filter(p => p.projeto !== NOME_RATEIO);
    const totalFatLiq     = demais.reduce((sum, p) => sum + p.faturamentoLiquido, 0);
    const calcPct         = (valor, base) =>
      base > 0 ? ((valor / base) * 100).toFixed(2) : '0.00';

    return demais.map(p => {
      const proporcao      = totalFatLiq > 0 ? p.faturamentoLiquido / totalFatLiq : 1 / demais.length;
      const custoRateado   = custoParaRatear * proporcao;
      const novoCustoOp    = p.custoOperacional + custoRateado;
      const novaMargemBruta= p.faturamentoLiquido - p.custoMaoDeObra - novoCustoOp;
      return {
        ...p,
        custoOperacional:      novoCustoOp,
        percentualCustoOp:     calcPct(novoCustoOp, p.faturamentoLiquido),
        margemBrutaReal:       novaMargemBruta,
        percentualMargemBruta: calcPct(novaMargemBruta, p.faturamentoLiquido),
      };
    });
  })();

  // ── dadosTabela — FONTE CANÔNICA ─────────────────────────────────────────────
  // No modo normal: listaProjetosTabela já está corretamente restrita ao escopo
  // do filtro ativo, então nenhum filtro adicional é necessário após o rateio.
  // No modo 2025: dadosTabela2025 deriva integralmente de dadosFiltrados.
  const dadosTabela = is2025 ? dadosTabela2025 : dadosTabelaNormalComRateio;

  // ── Ordenação ────────────────────────────────────────────────────────────────
  const handleOrdenar = (campo) => {
    setOrdenacao(prev => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const dadosOrdenados = useMemo(() => {
    const lista = [...dadosTabela];
    
    lista.sort((a, b) => {
      const valA = a[ordenacao.campo];
      const valB = b[ordenacao.campo];

      if (valA < valB) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (valA > valB) return ordenacao.direcao === 'asc' ? 1 : -1;
      return 0;
    });

    return lista;
  }, [dadosTabela, ordenacao]);

  // ── Helpers de formatação ────────────────────────────────────────────────────
  const formatCurrency = (value) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency', currency: 'BRL',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(value);


  // ── KPIs — derivados de dadosTabela ──────────────────────────────────────────
  const kpiFatLiq                 = dadosTabela.reduce((s, r) => s + r.faturamentoLiquido, 0);
  const kpiMargemTotal            = dadosTabela.reduce((s, r) => s + r.margemBrutaReal,    0);
  const kpiMargemPct              = kpiFatLiq > 0 ? (kpiMargemTotal / kpiFatLiq) * 100 : 0;


  // ── Tema / cores ─────────────────────────────────────────────────────────────
  const C = {
    brand:     '#A3003A',
    brandEscuro1: '#5C001F',
    brandEscuro2: '#7A0029',
    brandClaro1: '#C72C5C',
    brandClaro2: '#E85A84',

    liquido:   '#22C55E',
    custo:     '#D97706',
    azul:      '#3B82F6',
    roxo:      '#8B5CF6',
    vermelho:  '#DC2626',
    branco:     '#FFFFFF',
    bg:        isLight ? '#F3F4F6' : '#0F172A',
    card:      isLight ? '#FFFFFF' : '#1E293B',
    text:      isLight ? '#111827' : '#8c8e8fff',
    border:    isLight ? '#E5E7EB' : '#334155',
    muted:     isLight ? '#6B7280' : '#94A3B8',
    blueTint:  isLight ? '#EFF6FF' : '#1E3A5F',
    blueTint2: isLight ? '#DBEAFE' : '#1E3A5F',
    rowHover:  isLight ? '#F9FAFB' : '#273549',
    detailBg:  isLight ? '#F8FAFC' : '#0F172A',
    detailHead:isLight ? '#F1F5F9' : '#1E293B',
  };

  const IconeOrdenacao = ({ campo }) => {
    if (ordenacao.campo !== campo) return <ArrowUpDown size={14} className="text-gray-400" />;
    return ordenacao.direcao === 'asc'
      ? <ArrowUp   size={14} style={{ color: '#6366F1' }} />
      : <ArrowDown size={14} style={{ color: '#6366F1' }} />;
  };

  const detalhesPorProjeto = (projeto) =>
    dadosFiltrados.filter(item => item.projeto === projeto);

  const limparFiltros = () =>
    setFilters({ projeto: [], prestador: [], gerente: [], dataDre: [], anoDre: [] });

  const KpiCard = ({ label, value, sub, color }) => (
    <div style={{
      background: C.card, border: `1px solid ${C.border}`, borderTop: `3px solid ${color}`,
      borderRadius: 10, padding: '16px 18px', flex: 1, minWidth: 150,
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.3)',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.06em' }}>
        {label}
      </div>
      {loading
        ? <div style={{ height: 26, background: C.border, borderRadius: 4, animation: 'shimmer 1.4s ease-in-out infinite' }} />
        : <div style={{ fontSize: 21, fontWeight: 800, color: color || C.text }}>{value}</div>
      }
      {sub && !loading && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );


  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text, transition: 'background 0.25s' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes shimmer { 0%,100%{opacity:0.4} 50%{opacity:1} }
        .kpi-row   { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
        .chart-grid{ display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 15px; margin-top: 15px;  }
        @media (max-width: 1200px) { .chart-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 768px)  { .chart-grid { grid-template-columns: 1fr; } .kpi-row { gap: 8px; } }
        .tbl-row:hover td { background: ${C.rowHover} !important; }
        .th-sortable:hover { opacity: 0.8; cursor: pointer; }
      `}</style>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header style={{
        background: 'linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)',
        color: '#fff', padding: '14px 24px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
          <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => setIsLight(!isLight)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#fff', fontSize: 12, fontWeight: 600 }}
          >
            {isLight ? <Sun size={14} /> : <Moon size={14} />}
            {isLight ? 'Tema Claro' : 'Tema Escuro'}
          </button>
          <button
            onClick={() => navigate('/inicio')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 600 }}
          >
            Menu Principal
          </button>
        </div>
      </header>

      <div style={{ padding: '24px' }}>
        <div style={{ maxWidth: 1750, margin: '0 auto' }}>

          {/* ── Título ───────────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 4, height: 28, background: C.brand, borderRadius: 2 }} />
            <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, margin: 0 }}>Margem Bruta %</h1>
          </div>

          {/* ── Erro ─────────────────────────────────────────────────────────── */}
          {error && (
            <div style={{
              background: isLight ? '#FFFBEB' : '#451A03',
              border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 16px',
              marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <AlertCircle size={18} color="#D97706" />
              <p style={{ fontSize: 13, color: isLight ? '#92400E' : '#FCD34D', margin: 0 }}>
                <strong>Atenção:</strong> {error}
              </p>
            </div>
          )}

          {/* ── Filtros ──────────────────────────────────────────────────────── */}
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
            padding: 16, marginBottom: 20,
            boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.3)',
            }}>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4, fontWeight: 600 }}>Projeto</label>
                <MultiSelect
                  options={projetosOptions} placeholder="Projetos"
                  value={filters.projeto}
                  onChange={(v) => setFilters(f => ({ ...f, projeto: v }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4, fontWeight: 600 }}>Prestador de Serviço</label>
                <MultiSelect
                  options={prestadoresOptions} placeholder="Prestadores"
                  value={filters.prestador}
                  onChange={(v) => setFilters(f => ({ ...f, prestador: v }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4, fontWeight: 600 }}>Gerente de Projetos</label>
                <MultiSelect
                  options={gerentesOptions} placeholder="Gerentes"
                  value={filters.gerente}
                  onChange={(v) => setFilters(f => ({ ...f, gerente: v }))}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4, fontWeight: 600 }}>Mês DRE</label>
                <MultiSelect
                  options={mesesOptions} placeholder="Meses"
                  value={filters.dataDre}
                  onChange={(v) => setFilters(f => ({ ...f, dataDre: v }))}
                  text = "bkack"
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: C.muted, display: 'block', marginBottom: 4, fontWeight: 600 }}>Ano</label>
                <MultiSelect
                  options={anosOptions} placeholder="Anos"
                  value={filters.anoDre}
                  onChange={(v) => setFilters(f => ({ ...f, anoDre: v }))}
                />
              </div>
            </div>
            <button
              onClick={limparFiltros}
              style={{ fontSize: 13, color: C.brand, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              Limpar Filtros
            </button>
          </div>
                    {/* KPI Cards */}
          <div className="kpi-row">
            <KpiCard 
              label="% Margem Bruta" 
              value= {loading ? '' : `${kpiMargemPct.toFixed(1).replace('.', ',')}% `}
              color={kpiMargemTotal >= 0 ? C.liquido : C.vermelho}
              sub={loading ? '' : "Percentual dos Projetos"}
              />
          </div>

          {/* ═══════════════════════════════════════════════════════════════════
              SEÇÃO TABELA ANALÍTICA — idêntica ao margemBruta.js original
          ═══════════════════════════════════════════════════════════════════ */}
          <div style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16,
            boxShadow: isLight ? '0 1px 4px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', marginBottom: 16, letterSpacing: '0.06em' }}>
              Detalhamento Analítico por Projeto
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <RefreshCw className="animate-spin mx-auto mb-2" size={32} color={C.muted} />
                <p style={{ color: C.muted }}>Carregando dados...</p>
              </div>
            ) : dadosTabela.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted }}>
                Nenhum projeto encontrado com os filtros selecionados
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${C.border}` }}>

                      {/* Colunas base */}
                      {[
                        { label: 'Projeto',              campo: 'projeto',            align: 'left'  },
                        { label: 'Gerente',              campo: 'gerente',            align: 'left'  },
                      ].map(col => (
                        <th key={col.campo} className="th-sortable" onClick={() => handleOrdenar(col.campo)}
                          style={{ textAlign: col.align, padding: '10px 8px', color: C.muted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', userSelect: 'none', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}>
                            {col.label} <IconeOrdenacao campo={col.campo} />
                          </div>
                        </th>
                      ))}


                      {/* Margem */}
                      {[
                        { label: '% Margem Bruta',    campo: 'percentualMargemBruta' },
                      ].map(col => (
                        <th key={col.campo} className="th-sortable" onClick={() => handleOrdenar(col.campo)}
                          style={{ textAlign: 'right', padding: '10px 8px', color: C.muted, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', userSelect: 'none', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                            {col.label} <IconeOrdenacao campo={col.campo} />
                          </div>
                        </th>
                      ))}

                      {/* Expand */}
                      <th style={{ width: 40 }} />
                    </tr>
                  </thead>

                  <tbody>
                    {dadosOrdenados.map((row, idx) => {
                      const aberto = linhasExpandidas[row.projeto];
                      const pct    = Number(row.percentualMargemBruta);
                      const corMgm = pct >= 20 ? '#16A34A' : pct >= 0 ? C.text : C.vermelho;

                      return (
                        <React.Fragment key={idx}>
                          {/* Linha principal */}
                          <tr className="tbl-row" style={{ borderBottom: `1px solid ${C.border}` }}>
                            <td style={{ padding: '10px 8px', fontWeight: 600, color: C.text }}>{row.projeto}</td>
                            <td style={{ padding: '10px 8px', color: C.muted, fontSize: 12 }}>{row.gerente}</td>
                            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 700, color: corMgm }}>{row.percentualMargemBruta}%</td>
                            <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                            </td>
                          </tr>

                          {/* Detalhe expandido (NFe / Lançamentos 2025) */}
                          {aberto && (
                            <tr style={{ background: C.detailBg }}>
                              <td colSpan={is2025 ? 11 : 12} style={{ padding: '12px 24px' }}>
                                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                                  <thead>
                                    <tr style={{ background: C.detailHead }}>
                                      <th style={{ textAlign: 'left',   padding: '8px 10px', color: C.muted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>Prestador</th>
                                      <th style={{ textAlign: 'left',   padding: '8px 10px', color: C.muted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>Gerente</th>
                                      <th style={{ textAlign: 'right',  padding: '8px 10px', color: C.muted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>Valor Bruto</th>
                                      <th style={{ textAlign: 'right',  padding: '8px 10px', color: C.muted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>Fat. Líquido</th>
                                      <th style={{ textAlign: 'center', padding: '8px 10px', color: C.muted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>Mês/Ano</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detalhesPorProjeto(row.projeto).map((det, i) => (
                                      <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                                        <td style={{ padding: '7px 10px', color: C.text }}>{det.prestador || '—'}</td>
                                        <td style={{ padding: '7px 10px', color: C.text }}>{det.gerente}</td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right', color: C.text }}>{formatCurrency(det.valorBruto)}</td>
                                        <td style={{ padding: '7px 10px', textAlign: 'right', color: C.text }}>{formatCurrency(det.faturamentoLiquido)}</td>
                                        <td style={{ padding: '7px 10px', textAlign: 'center', color: C.muted }}>
                                          {det.dataDre || det.mes}/{det.anoDre}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>

                  {/* Footer — Totais */}
                  <tfoot>
                    <tr style={{ borderTop: `2px solid ${C.border}`, background: C.detailHead, fontWeight: 800 }}>
                      <td colSpan={2} style={{ padding: '10px 8px', color: C.text, fontSize: 12 }}>TOTAL GERAL</td>
                      <td style={{ padding: '10px 8px', textAlign: 'right', color: kpiMargemTotal >= 0 ? '#16A34A' : C.vermelho }}>
                        {(() => {
                          const tfl = dadosOrdenados.reduce((a, r) => a + r.faturamentoLiquido, 0);
                          const tm  = dadosOrdenados.reduce((a, r) => a + r.margemBrutaReal,    0);
                          return tfl > 0 ? ((tm / tfl) * 100).toFixed(2) : '0.00';
                        })()}%
                      </td>
                      {!is2025 && <td />}
                      <td />
                    </tr>
                  </tfoot>

                </table>
              </div>
            )}
          </div>{/* /tabela */}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;